import { DEFAULT_WELLS, CONVERSION_CONSTANTS, MAX_CONCENTRATIONS } from 'constants/experimentDefaults'
import { useEffect, useState, useMemo, useRef } from 'react'
import { Box, Grid, useTheme } from '@mui/material'
import { useDebounce } from 'use-debounce'
import { useQuery } from 'react-query'
import useNotificationSlice from 'redux/slices/hooks/useNotificationSlice'
import useEventManager from 'redux/slices/hooks/useEventsManager'
import { notificationComponent } from 'redux/slices'
import { calculateRunMetrics, calculateRunMetricsVersioned, RMERequest } from 'utils/api'
import { validateWell } from 'utils/experimentValidation'
import { useRmeV18Access } from 'utils/useRmeV18Access'
import { useFeatureFlags } from 'services/featureFlags'
import { ExperimentHandlers } from './types'
import DefineExperiment from './DefineExperiment'
import PrepareSample from './PrepareSample'
import RunInstructions from './RunInstructions'
import useExperimentPlanningParams from '../../utils/useExperimentPlanningParams'

const RunMetricsEstimator = (): React.ReactElement => {
  const theme = useTheme()
  const events = useEventManager()
  const { experimentData, setExperimentData } = useExperimentPlanningParams()
  const isRmeV18 = useRmeV18Access()
  const featureFlags = useFeatureFlags()
  const areFlagsLoaded = featureFlags.isLoaded()
  const [isInitialized, setIsInitialized] = useState(false)
  const prevQueryKeyRef = useRef<string | null>(null)
  
  const getInitialWells = () => {
    const urlParams = new URLSearchParams(window.location.search)
    // Try to get wells from either purityWells or yieldWells params
    const wellsParam = urlParams.get('purityWells') || urlParams.get('yieldWells')
    
    if (!wellsParam) return DEFAULT_WELLS
    
    try {
      const wellsData = wellsParam.split(',').map((wellStr: string) => {
        const [id, outputCells, estimatedFrequency] = wellStr.split(':')
        return {
          id: parseInt(id, 10),
          outputCells: parseInt(outputCells, 10) || 0,
          estimatedFrequency: parseFloat(estimatedFrequency) || 0
        }
      })
      
      return DEFAULT_WELLS.map(well => {
        const urlWell = wellsData.find((w: { id: number; outputCells: number; estimatedFrequency: number }) => w.id === well.id)
        return urlWell || well
      })
    } catch (error) {
      console.error('Failed to parse wells from URL:', error)
      return DEFAULT_WELLS
    }
  }

  // Use shared wells for both purity and yield modes
  const [sortingWellConfig, setSortingWellConfig] = useState(() => getInitialWells())
  
  const { showError, clearNotification } = useNotificationSlice()
  
  const [debouncedCellsToImage] = useDebounce(experimentData.cellsToImage, 500)
  const [debouncedCellConcentration] = useDebounce(experimentData.cellConcentration, 500)
  const [debouncedMinInputSampleVolume] = useDebounce(experimentData.minInputSampleVolume, 500)
  
  // Use shared wells for both purity and yield modes
  const currentWells = experimentData.experimentGoal === 'imaging' ? [] : sortingWellConfig
  const [debouncedWellsData] = useDebounce(currentWells, 500)

  // Build API request function
  const buildApiRequest = (): RMERequest => {
    if (experimentData.experimentGoal === 'imaging') {
      // Validate concentration for imaging mode (0.3M/ml to 10M/ml)
      const concentration = Math.max(0.3, Math.min(10, debouncedCellConcentration))
      
      return {
        run_type: 'imaging',
        concentration_cells_per_ml: concentration * CONVERSION_CONSTANTS.CELLS_PER_ML_TO_MILLION, // Convert M/ml to cells/ml
        number_of_cells: debouncedCellsToImage
      }
    }
    
    return {
      run_type: 'sorting',
      concentration_cells_per_ml: debouncedCellConcentration * CONVERSION_CONSTANTS.CELLS_PER_ML_TO_MILLION,
      sort_mode: experimentData.experimentGoal === 'purity' ? 'high_purity' : 'high_yield',
      well_specifications: debouncedWellsData
        .filter(well => well.outputCells > 0)
        .map(well => ({
          target_cell_count: well.outputCells,
          positive_cell_fraction: well.estimatedFrequency / CONVERSION_CONSTANTS.PERCENTAGE_TO_FRACTION // Convert percentage to fraction
        }))
    }
  }
  
  // Build React Query key based on all relevant parameters
  const queryKey = useMemo(() => {
    return [
      'calculateRunMetrics',
      {
        experimentGoal: experimentData.experimentGoal,
        cellsToImage: debouncedCellsToImage,
        cellConcentration: debouncedCellConcentration,
        minInputSampleVolume: debouncedMinInputSampleVolume,
        wells: debouncedWellsData,
        rmeVersion: isRmeV18 ? '1.8' : undefined
      }
    ]
  }, [experimentData.experimentGoal, debouncedCellsToImage, debouncedCellConcentration, debouncedMinInputSampleVolume, debouncedWellsData, isRmeV18])

  // Check if we should call API based on experiment goal and data validity
  const shouldCallApi = useMemo(() => {
    if (!isInitialized || !areFlagsLoaded) return false
    
    if (experimentData.experimentGoal === 'imaging') {
      return debouncedCellsToImage > 0 && 
             debouncedCellConcentration >= 0.3 && 
             debouncedCellConcentration <= 10
    }
    
    // For sorting modes, check that we have complete valid wells
    const validWells = debouncedWellsData.filter(well => 
      well.outputCells > 0 && well.estimatedFrequency > 0
    )
    
    // Only call API if we have at least one valid well AND no incomplete wells
    const hasValidWells = validWells.length > 0
    const hasIncompleteWells = debouncedWellsData.some(well => 
      (well.outputCells > 0 && well.estimatedFrequency === 0) ||
      (well.outputCells === 0 && well.estimatedFrequency > 0)
    )
    
    // Check for validation errors in complete wells (only for sorting modes)
    let hasValidationErrors = false
    if (experimentData.experimentGoal === 'purity') {
      hasValidationErrors = validWells.some(well => {
        const validation = validateWell(well, 'purity')
        return validation.hasOutputCellsError || validation.hasFrequencyError
      })
    } else if (experimentData.experimentGoal === 'yield') {
      hasValidationErrors = validWells.some(well => {
        const validation = validateWell(well, 'yield')
        return validation.hasOutputCellsError || validation.hasFrequencyError
      })
    }
    
    return hasValidWells && !hasIncompleteWells && !hasValidationErrors
  }, [isInitialized, areFlagsLoaded, experimentData.experimentGoal, debouncedCellsToImage, debouncedCellConcentration, debouncedWellsData])
  
  // Track experiment parameter changes before API call
  useEffect(() => {
    if (shouldCallApi) {
      const currentQueryKey = JSON.stringify(queryKey)
      if (prevQueryKeyRef.current !== currentQueryKey) {
        prevQueryKeyRef.current = currentQueryKey

        events.sendExperimentGoalChanged({
          experimentGoal: experimentData.experimentGoal,
          cellConcentration: debouncedCellConcentration,
          cellsToImage: experimentData.experimentGoal === 'imaging' ? debouncedCellsToImage : undefined,
          wells: experimentData.experimentGoal !== 'imaging' ? debouncedWellsData : undefined,
        })
      }
    }
  }, [shouldCallApi, queryKey, experimentData.experimentGoal, debouncedCellConcentration, debouncedCellsToImage, debouncedWellsData, events])

  const { data: apiResponse, isLoading, isFetching } = useQuery(
    queryKey,
    async () => {
      const request = buildApiRequest()
      try {
        return await calculateRunMetricsVersioned(request, isRmeV18 ? '1.8' : '1.4')
      } catch {
        // Fallback to unversioned endpoint if versioned is not deployed yet
        return await calculateRunMetrics(request)
      }
    },
    {
      enabled: shouldCallApi,
      keepPreviousData: true,
      staleTime: 1000,
      retry: false,
      onError: (err: Error) => {
        const errorMessage = err.message || 'Failed to calculate metrics'
        showError(errorMessage, notificationComponent.Toaster)
      },
      onSuccess: () => {
        clearNotification()
      }
    }
  )
  
  // Show loading during initial load or subsequent fetches
  const isLoadingMetrics = isLoading || isFetching
  
  // Extract data from API response
  const metricsResult = apiResponse?.data || null

  // Set initialization flag
  useEffect(() => {
    setIsInitialized(true)
  }, [])

  // Clear error notifications when key experiment parameters change
  useEffect(() => {
    if (isInitialized) {
      clearNotification()
    }
  }, [experimentData.experimentGoal, debouncedCellsToImage, debouncedCellConcentration, debouncedWellsData, clearNotification, isInitialized])

  // Update experiment data for API calls and URL
  useEffect(() => {
    if (isInitialized && (experimentData.experimentGoal === 'purity' || experimentData.experimentGoal === 'yield')) {
      setExperimentData(prev => ({
        ...prev,
        wells: [...debouncedWellsData]
      }))
    }
  }, [debouncedWellsData, setExperimentData, experimentData.experimentGoal, isInitialized])


  const handlers: ExperimentHandlers = {
    handleExperimentGoalChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const newGoal = event.target.value as 'imaging' | 'purity' | 'yield'

      setExperimentData(prev => {
        // Cap concentration if switching to sorting mode and current value exceeds max
        const newMaxConcentration = MAX_CONCENTRATIONS[newGoal]
        const cappedConcentration = prev.cellConcentration > newMaxConcentration
          ? newMaxConcentration
          : prev.cellConcentration

        return {
          ...prev,
          experimentGoal: newGoal,
          cellConcentration: cappedConcentration
        }
      })
    },

    handleCellsToImageChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      setExperimentData(prev => ({ ...prev, cellsToImage: parseInt(event.target.value, 10) || 0 }))
    },

    handleCellConcentrationChange: (_event: Event, newValue: number | number[]) => {
      const concentration = newValue as number
      setExperimentData(prev => ({
        ...prev,
        cellConcentration: concentration
      }))
    },

    handleWellOutputChange: (wellId: number, value: string) => {
      const numValue = parseInt(value, 10) || 0
      setSortingWellConfig(prev =>
        prev.map(well => 
          well.id === wellId ? { ...well, outputCells: numValue } : well
        )
      )
    },

    handleWellFrequencyChange: (wellId: number, value: string) => {
      const numValue = value === '' ? 0 : parseFloat(value) || 0
      setSortingWellConfig(prev =>
        prev.map(well => 
          well.id === wellId ? { ...well, estimatedFrequency: numValue } : well
        )
      )
    },

    handleMinInputSampleVolumeChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const numValue = parseFloat(event.target.value) || 0
      setExperimentData(prev => ({
        ...prev,
        minInputSampleVolume: numValue,
        [`${prev.experimentGoal}SampleVolume`]: numValue
      }))
    }
  }

  return (
    <Box sx={{ 
      p: 3, 
      height: '100vh', 
      overflow: 'auto',
      boxSizing: 'border-box',
      backgroundColor: theme.palette.background.default
    }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <DefineExperiment 
            experimentData={{...experimentData, wells: currentWells}}
            handlers={handlers}
            theme={theme}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <PrepareSample 
            experimentData={experimentData}
            handlers={handlers}
            metricsResult={metricsResult}
            theme={theme}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <RunInstructions 
            experimentData={{...experimentData, wells: currentWells}}
            metricsResult={metricsResult}
            isLoadingMetrics={isLoadingMetrics}
            theme={theme}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default RunMetricsEstimator